/**
 * Ticket Queue Page (Admin)
 *
 * Admin page for managing all support tickets.
 * Includes filtering, bulk assignment, and ticket status management.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Users } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { EmptyState } from '../../components/common/EmptyState';
import { TicketCard } from '../../components/support/TicketCard';
import { TicketFilters } from '../../components/support/TicketFilters';
import type {
  SupportTicket,
  TicketType,
  TicketStatus,
  TicketPriority,
} from '../../types/support';

export const TicketQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());

  // Filters
  const [typeFilter, setTypeFilter] = useState<TicketType | 'all'>(
    (searchParams.get('type') as TicketType) || 'all'
  );
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>(
    (searchParams.get('status') as TicketStatus) || 'all'
  );
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | 'all'>(
    (searchParams.get('priority') as TicketPriority) || 'all'
  );
  const [assigneeFilter, setAssigneeFilter] = useState<string>(
    searchParams.get('assignedTo') || 'all'
  );

  useEffect(() => {
    loadTickets();
  }, [typeFilter, statusFilter, priorityFilter, assigneeFilter, searchQuery]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call when Phase 1E is implemented
      // const response = await mockApi.getTickets({
      //   type: typeFilter !== 'all' ? typeFilter : undefined,
      //   status: statusFilter !== 'all' ? statusFilter : undefined,
      //   priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      //   assignedTo: assigneeFilter !== 'all' ? assigneeFilter : undefined,
      //   search: searchQuery || undefined,
      // });

      // Mock empty data for now
      setTickets([]);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = (ticketId: string) => {
    const newSelection = new Set(selectedTickets);
    if (newSelection.has(ticketId)) {
      newSelection.delete(ticketId);
    } else {
      newSelection.add(ticketId);
    }
    setSelectedTickets(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map((t) => t.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (selectedTickets.size === 0) return;

    // TODO: Show assignment modal
    console.log('Bulk assign', Array.from(selectedTickets));
  };

  const handleBulkStatusChange = async (newStatus: TicketStatus) => {
    if (selectedTickets.size === 0) return;

    // TODO: Update ticket statuses
    console.log('Bulk status change', newStatus, Array.from(selectedTickets));
  };

  const assigneeOptions = [
    { value: 'all', label: 'All Assignees' },
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'me', label: 'Assigned to Me' },
    // TODO: Load support staff from API
  ];

  const filteredTickets = tickets.filter((ticket) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.ticketNumber.toLowerCase().includes(query) ||
        ticket.subject.toLowerCase().includes(query) ||
        ticket.companyName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <PageLayout
      pageTitle="Ticket Queue"
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Admin', href: '/admin/dashboard' },
        { label: 'Support', href: '/admin/support' },
        { label: 'Tickets' },
      ]}
    >
      <Card className="mb-6">
        <CardBody>
          {/* Search */}
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Search by ticket number, subject, or company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-neutral-600">
              <Filter className="w-5 h-5" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <TicketFilters
              type={typeFilter}
              status={statusFilter}
              priority={priorityFilter}
              onTypeChange={setTypeFilter}
              onStatusChange={setStatusFilter}
              onPriorityChange={setPriorityFilter}
            />

            {/* Assignee Filter */}
            <Select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="min-w-[180px]"
            >
              {assigneeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">
                All Tickets
                {filteredTickets.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-neutral-600">
                    ({filteredTickets.length})
                  </span>
                )}
              </h3>

              {selectedTickets.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">
                    {selectedTickets.size} selected
                  </span>
                  <Button variant="outline" size="sm" icon={<Users className="w-4 h-4" />} onClick={handleBulkAssign}>
                    Assign
                  </Button>
                  <Select
                    value=""
                    onChange={(e) => handleBulkStatusChange(e.target.value as TicketStatus)}
                    className="w-40"
                  >
                    <option value="">Change Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </Select>
                </div>
              )}
            </div>

            {tickets.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {selectedTickets.size === tickets.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-32 bg-neutral-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : filteredTickets.length === 0 ? (
            <EmptyState
              icon={<Search className="w-10 h-10" />}
              title={searchQuery ? 'No Tickets Found' : 'No Tickets'}
              description={
                searchQuery
                  ? 'Try adjusting your search or filters.'
                  : 'No support tickets to display.'
              }
            />
          ) : (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div key={ticket.id} className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedTickets.has(ticket.id)}
                    onChange={() => handleSelectTicket(ticket.id)}
                    className="mt-4 w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <TicketCard
                      ticket={ticket}
                      showCompany
                      onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

